import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Collapse,
  FormControlLabel,
  Checkbox,
  Stack,
} from '@mui/material';
import { DesktopTimePicker } from '@mui/x-date-pickers';
import { Edit, Cancel } from '@mui/icons-material';
import { BusinessHoursEntry, DayKey, WALK_IN_NOTE_LABEL } from '../../utils/businessHours';
import { TIME_INTERVAL } from '../../constants';

interface BusinessHoursCardProps {
  day: DayKey;
  entry: BusinessHoursEntry;
  onChange: (next: BusinessHoursEntry) => void;
  error?: string;
  disabled?: boolean;
}

const BusinessHoursCard: React.FC<BusinessHoursCardProps> = ({
  day,
  entry,
  onChange,
  error,
  disabled = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (disabled) {
      setIsEditing(false);
    }
  }, [disabled]);

  const dayLabel = day.charAt(0).toUpperCase() + day.slice(1);

  const handleClosedChange = (checked: boolean) => {
    onChange({
      ...entry,
      isClosed: checked,
      walkInOnly: checked ? false : entry.walkInOnly,
    });
  };

  const handleTimeChange = (key: 'startTime' | 'endTime', newValue: BusinessHoursEntry['startTime']) => {
    onChange({
      ...entry,
      [key]: newValue,
      isClosed: newValue ? false : entry.isClosed,
    });
  };

  const handleWalkInChange = (checked: boolean) => {
    onChange({
      ...entry,
      walkInOnly: checked,
    });
  };

  const toggleEdit = () => {
    if (disabled) return;
    setIsEditing((prev) => !prev);
  };

  return (
    <Card sx={{ mb: 2, borderRadius: '12px', boxShadow: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ textTransform: 'uppercase' }}>
            {dayLabel}
          </Typography>
          <IconButton onClick={toggleEdit} disabled={disabled}>
            {isEditing ? <Cancel /> : <Edit />}
          </IconButton>
        </Box>

        <Collapse in={!isEditing}>
          <Box mt={2}>
            {entry.isClosed ? (
              <Typography fontStyle="italic" fontWeight={600}>
                Closed
              </Typography>
            ) : (
              <Stack spacing={0.5}>
                <Typography>
                  <Box component="span" fontWeight={600}>
                    Hours:
                  </Box>{' '}
                  {entry.startTime?.format('h:mm A') ?? 'N/A'} - {entry.endTime?.format('h:mm A') ?? 'N/A'}
                </Typography>
                {entry.walkInOnly && (
                  <Typography variant="body2" color="text.secondary">
                    {WALK_IN_NOTE_LABEL}
                  </Typography>
                )}
              </Stack>
            )}
          </Box>
        </Collapse>

        <Collapse in={isEditing}>
          <Stack spacing={2} mt={2}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={entry.isClosed}
                    onChange={(e) => handleClosedChange(e.target.checked)}
                    disabled={disabled}
                  />
                }
                label="Closed"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={entry.walkInOnly}
                    onChange={(e) => handleWalkInChange(e.target.checked)}
                    disabled={entry.isClosed || disabled}
                  />
                }
                label={WALK_IN_NOTE_LABEL}
              />
            </Stack>
            <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'nowrap' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <DesktopTimePicker
                  label="Start Time"
                  value={entry.startTime}
                  onChange={(newValue) => handleTimeChange('startTime', newValue)}
                  disabled={entry.isClosed || disabled}
                  minutesStep={TIME_INTERVAL}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <DesktopTimePicker
                  label="End Time"
                  value={entry.endTime}
                  onChange={(newValue) => handleTimeChange('endTime', newValue)}
                  disabled={entry.isClosed || disabled}
                  minutesStep={TIME_INTERVAL}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Box>
            </Stack>
            {error && (
              <Typography variant="caption" color="error">
                {error}
              </Typography>
            )}
          </Stack>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default BusinessHoursCard;
